/**
 * VÉRAULT Portal — Client: Settings (Wave 26.13 — VIP)
 *
 * A quiet preferences room. The client manages notification cadence,
 * preferred language, timezone, and contact number. Name + email remain
 * advisor-managed.
 *
 * Also surfaces their prior trip-request wishes so they feel heard.
 */
import React from "react";
import { motion } from "framer-motion";
import {
  Settings as SettingsIcon,
  Bell,
  Mail,
  MessageSquare,
  Phone,
  Globe2,
  Clock,
  CheckCircle2,
  Loader2,
  Sparkles,
  CalendarRange,
} from "lucide-react";
import {
  Surface,
  Heading,
  PageHeader,
  EditorialRule,
  PortalButton,
  Empty,
  Skeleton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";
import { Link } from "react-router-dom";
import { RequestTripModal } from "../../components/RequestTripModal";

const { fadeInUp, stagger } = portalMotion;

const inputCls =
  "w-full px-3.5 py-2.5 rounded-md bg-white border border-[rgba(0,0,0,0.1)] " +
  "font-body text-[14px] text-[hsl(var(--foreground))] " +
  "placeholder:text-[hsl(var(--foreground-muted))] placeholder:italic placeholder:font-accent " +
  "focus:outline-none focus:border-[hsl(var(--accent-gold))] focus:ring-1 focus:ring-[hsl(var(--accent-gold))]/30 " +
  "transition-colors";

const labelCls =
  "block font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1.5";

const PREF_ROWS = [
  {
    key: "email_trip_updates",
    icon: Mail,
    label: "Email — trip updates",
    hint: "Reveal announcements, itinerary changes, document drops.",
  },
  {
    key: "email_concierge_messages",
    icon: MessageSquare,
    label: "Email — concierge messages",
    hint: "When your advisor responds in the concierge thread.",
  },
  {
    key: "email_marketing",
    icon: Sparkles,
    label: "Email — quiet inspiration",
    hint: "Occasional editorial — never a hard sell.",
  },
  {
    key: "sms_critical",
    icon: Phone,
    label: "SMS — critical only",
    hint: "Day-of-travel flight or hotel disruptions.",
  },
];

function PrefToggle({ row, value, onChange, disabled }) {
  const Icon = row.icon;
  return (
    <div
      className="flex items-start justify-between gap-4 py-3.5 border-b border-[rgba(0,0,0,0.04)] last:border-b-0"
      data-testid={`pref-row-${row.key}`}
    >
      <div className="flex items-start gap-3 min-w-0">
        <Icon className="size-4 mt-0.5 text-[hsl(var(--accent-gold))] shrink-0" strokeWidth={1.5} />
        <div className="min-w-0">
          <p className="font-body text-[14px] text-[hsl(var(--foreground))]">{row.label}</p>
          <p className="mt-0.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
            {row.hint}
          </p>
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={!!value}
        disabled={disabled}
        onClick={() => onChange(!value)}
        className={`relative shrink-0 w-11 h-6 rounded-full transition-colors duration-200 disabled:opacity-40 ${
          value
            ? "bg-[hsl(var(--accent-gold))]"
            : "bg-[hsl(var(--background-stone))] border border-[rgba(0,0,0,0.08)]"
        }`}
        data-testid={`pref-toggle-${row.key}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
            value ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </div>
  );
}

function PastWishes({ refreshKey }) {
  const [wishes, setWishes] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    api
      .get("/client-portal/trip-requests")
      .then(({ data }) => alive && setWishes(Array.isArray(data) ? data : []))
      .catch(() => alive && setWishes([]));
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  if (wishes === null) return <Skeleton className="h-24" />;
  if (wishes.length === 0) return null;

  const statusLabel = {
    pending: "With your advisor",
    converted: "Becoming an experience",
    dismissed: "Set aside",
  };

  return (
    <motion.div variants={fadeInUp} data-testid="past-wishes-section">
      <EditorialRule label="Wishes you've sent" />
      <Surface variant="default" padded className="!p-5 mt-5">
        <ul className="divide-y divide-[rgba(0,0,0,0.04)]">
          {wishes.map((w) => (
            <li key={w.id} className="py-3 first:pt-0 last:pb-0" data-testid={`wish-${w.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-[16px] text-[hsl(var(--foreground))]">
                    {w.destination_idea}
                  </p>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                    {[w.timeframe, w.occasion].filter(Boolean).join(" · ") || "Open timing"}
                    {" · "}
                    {fmt.formatDate ? fmt.formatDate(w.created_at) : (w.created_at || "").slice(0, 10)}
                  </p>
                </div>
                <span
                  className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-[0.18em] ${
                    w.status === "converted"
                      ? "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))]/30"
                      : w.status === "dismissed"
                        ? "bg-[hsl(var(--background-stone))] text-[hsl(var(--foreground-muted))] border border-[rgba(0,0,0,0.06)]"
                        : "bg-white text-[hsl(var(--foreground-secondary))] border border-[rgba(0,0,0,0.08)]"
                  }`}
                >
                  {statusLabel[w.status] || w.status}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </Surface>
    </motion.div>
  );
}

export default function ClientSettings() {
  const [profile, setProfile] = React.useState(null);
  const [prefs, setPrefs] = React.useState(null);
  const [editable, setEditable] = React.useState({ phone: "", preferred_language: "", timezone: "" });
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [savedAt, setSavedAt] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [requestOpen, setRequestOpen] = React.useState(false);
  const [wishesRefresh, setWishesRefresh] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    api
      .get("/client-portal/settings")
      .then(({ data }) => {
        if (!alive) return;
        setProfile(data);
        setPrefs(data.notification_preferences || {});
        setEditable({
          phone: data.phone || "",
          preferred_language: data.preferred_language || "",
          timezone: data.timezone || "",
        });
      })
      .catch(() => alive && setError("Couldn't load your settings."));
    return () => {
      alive = false;
    };
  }, []);

  const updatePref = (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    setDirty(true);
  };
  const updateField = (key) => (e) => {
    setEditable((f) => ({ ...f, [key]: e.target.value }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.patch("/client-portal/settings", {
        notification_preferences: prefs,
        phone: editable.phone,
        preferred_language: editable.preferred_language,
        timezone: editable.timezone,
      });
      setDirty(false);
      setSavedAt(new Date());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save changes.");
    } finally {
      setSaving(false);
    }
  };

  if (error && !profile) {
    return (
      <Empty
        title={error}
        icon={<SettingsIcon className="size-6" strokeWidth={1.25} />}
      />
    );
  }

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-12" data-testid="client-settings-page">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Settings"
          title="Your preferences."
          lede="Quiet controls for how we stay in touch — and a place to send us your next idea."
        />
      </motion.div>

      {/* Request a future trip — primary CTA */}
      <motion.div variants={fadeInUp}>
        <div className="grid lg:grid-cols-2 gap-4">
          <Surface
            variant="default"
            padded
            className="!p-6"
            data-testid="request-trip-cta-card"
          >
            <div className="flex flex-col h-full">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
                Another experience?
              </p>
              <h3 className="mt-2 font-serif text-[22px] text-[hsl(var(--foreground))]">
                Send us your next dream.
              </h3>
              <p className="mt-2 font-accent italic text-[14.5px] text-[hsl(var(--foreground-secondary))] flex-1">
                Share a place, a season, or just a feeling. We'll reach out to begin shaping it.
              </p>
              <div className="mt-4">
                <PortalButton
                  variant="primary"
                  size="lg"
                  icon={<Sparkles className="size-4" strokeWidth={1.5} />}
                  onClick={() => setRequestOpen(true)}
                  data-testid="settings-request-trip-cta"
                >
                  Request a trip
                </PortalButton>
              </div>
            </div>
          </Surface>

          <Surface
            variant="default"
            padded
            className="!p-6"
            data-testid="schedule-call-cta-card"
          >
            <div className="flex flex-col h-full">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
                Want to talk it through?
              </p>
              <h3 className="mt-2 font-serif text-[22px] text-[hsl(var(--foreground))]">
                Schedule a call.
              </h3>
              <p className="mt-2 font-accent italic text-[14.5px] text-[hsl(var(--foreground-secondary))] flex-1">
                A quiet 45-minute conversation with your advisor — booked directly from your suite.
              </p>
              <div className="mt-4">
                <PortalButton
                  as={Link}
                  to="/portal/client/schedule"
                  variant="primary"
                  size="lg"
                  icon={<CalendarRange className="size-4" strokeWidth={1.5} />}
                  data-testid="settings-schedule-cta"
                >
                  Find a time
                </PortalButton>
              </div>
            </div>
          </Surface>
        </div>
      </motion.div>

      {/* Notification preferences */}
      <motion.div variants={fadeInUp}>
        <EditorialRule label="How we stay in touch" />
        <Surface variant="default" padded className="!p-5 mt-5" data-testid="notification-prefs-card">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
            <Heading level="eyebrow">Notifications</Heading>
          </div>
          {prefs === null ? (
            <Skeleton className="h-40" />
          ) : (
            <div className="mt-2">
              {PREF_ROWS.map((row) => (
                <PrefToggle
                  key={row.key}
                  row={row}
                  value={prefs[row.key] ?? false}
                  onChange={(v) => updatePref(row.key, v)}
                  disabled={saving}
                />
              ))}
            </div>
          )}
        </Surface>
      </motion.div>

      {/* Profile fields */}
      <motion.div variants={fadeInUp}>
        <EditorialRule label="About you" />
        <Surface variant="default" padded className="!p-5 mt-5" data-testid="profile-fields-card">
          {profile === null ? (
            <Skeleton className="h-32" />
          ) : (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <span className={labelCls}>Name</span>
                  <p className="font-serif text-[16px] text-[hsl(var(--foreground))]">
                    {profile.full_name || "—"}
                  </p>
                  <p className="mt-1 font-accent italic text-[11.5px] text-[hsl(var(--foreground-muted))]">
                    Managed by your advisor.
                  </p>
                </div>
                <div>
                  <span className={labelCls}>Email</span>
                  <p className="font-body text-[14px] text-[hsl(var(--foreground))] truncate">
                    {profile.email || "—"}
                  </p>
                  <p className="mt-1 font-accent italic text-[11.5px] text-[hsl(var(--foreground-muted))]">
                    Managed by your advisor.
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-[rgba(0,0,0,0.04)]">
                <div>
                  <label className={labelCls} htmlFor="cs-phone">
                    <Phone className="inline size-3 mr-1 -mt-0.5" strokeWidth={1.5} /> Phone
                  </label>
                  <input
                    id="cs-phone"
                    type="tel"
                    value={editable.phone}
                    onChange={updateField("phone")}
                    placeholder="+1 555 0123"
                    className={inputCls}
                    data-testid="settings-phone"
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="cs-language">
                    <Globe2 className="inline size-3 mr-1 -mt-0.5" strokeWidth={1.5} /> Preferred language
                  </label>
                  <select
                    id="cs-language"
                    value={editable.preferred_language}
                    onChange={updateField("preferred_language")}
                    className={inputCls + " appearance-none cursor-pointer"}
                    data-testid="settings-language"
                  >
                    <option value="">English (default)</option>
                    <option value="en">English</option>
                    <option value="fr">Français</option>
                    <option value="es">Español</option>
                    <option value="it">Italiano</option>
                    <option value="de">Deutsch</option>
                    <option value="pt">Português</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="cs-timezone">
                    <Clock className="inline size-3 mr-1 -mt-0.5" strokeWidth={1.5} /> Timezone
                  </label>
                  <input
                    id="cs-timezone"
                    type="text"
                    value={editable.timezone}
                    onChange={updateField("timezone")}
                    placeholder="America/New_York · Europe/Paris"
                    className={inputCls}
                    data-testid="settings-timezone"
                  />
                </div>
              </div>
            </div>
          )}
        </Surface>
      </motion.div>

      <PastWishes refreshKey={wishesRefresh} />

      {/* Save bar — appears when dirty */}
      <motion.div
        variants={fadeInUp}
        className="sticky bottom-4 z-30"
        animate={{ opacity: dirty || savedAt ? 1 : 0, y: dirty || savedAt ? 0 : 20 }}
        style={{ pointerEvents: dirty || savedAt ? "auto" : "none" }}
        data-testid="settings-save-bar"
      >
        <div className="flex items-center justify-between gap-4 px-5 py-3 rounded-full bg-[hsl(var(--foreground))] text-[hsl(var(--primary-foreground))] shadow-floating">
          <p className="font-accent italic text-[13px]">
            {savedAt ? (
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="size-3.5" strokeWidth={1.5} /> Saved
              </span>
            ) : (
              "You have unsaved changes."
            )}
          </p>
          {dirty && (
            <PortalButton
              type="button"
              variant="gold"
              size="sm"
              onClick={save}
              disabled={saving}
              icon={saving ? <Loader2 className="size-3.5 animate-spin" strokeWidth={1.5} /> : null}
              data-testid="settings-save-btn"
            >
              {saving ? "Saving…" : "Save changes"}
            </PortalButton>
          )}
        </div>
        {error && (
          <p className="mt-2 text-center font-body text-[12.5px] text-[hsl(var(--accent-terracotta))]">
            {error}
          </p>
        )}
      </motion.div>

      <RequestTripModal
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        onSubmitted={() => setWishesRefresh((n) => n + 1)}
      />
    </motion.div>
  );
}
